import { motion } from 'framer-motion'

import { styled } from 'styled-system/jsx'

const Backdrop = styled(motion.div, {
  base: {
    position: 'fixed',
    inset: '0',
    backgroundColor: 'rgba(26, 19, 48, 0.45)',
    zIndex: '90',
    display: 'flex',
    justifyContent: 'flex-end',
  },
})

const Panel = styled(motion.aside, {
  base: {
    width: '100%',
    maxWidth: '420px',
    height: '100%',
    backgroundColor: '#FFFFFF',
    boxShadow: '-24px 0 60px -20px rgba(26, 19, 48, 0.28)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
})

const Header = styled('header', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingX: '20px',
    paddingY: '18px',
    borderBottom: '1px solid',
    borderColor: '#F0EDFA',
  },
})

const HeaderText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
})

const Title = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.3px',
  },
})

const Subtitle = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
  },
})

const HeaderActions = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
})

const TextButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: 'sm',
    transition:
      'color {durations.fast} {easings.appleStandard}, background-color {durations.fast} {easings.appleStandard}',
    _hover: {
      color: 'quickpass.primary',
      backgroundColor: 'quickpass.surfaceCool',
    },
    _disabled: {
      opacity: '0.45',
      cursor: 'not-allowed',
      _hover: { backgroundColor: 'transparent', color: 'quickpass.inkSoft' },
    },
  },
})

const CloseButton = styled('button', {
  base: {
    width: '36px',
    height: '36px',
    borderRadius: 'pill',
    border: 'none',
    backgroundColor: 'quickpass.surfaceCool',
    color: 'quickpass.inkSoft',
    fontSize: '18px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition:
      'background-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: {
      backgroundColor: 'quickpass.warmth',
      color: '#FFFFFF',
    },
  },
})

const ScrollArea = styled('div', {
  base: {
    flex: '1',
    overflowY: 'auto',
    paddingX: '12px',
    paddingY: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const SectionLabel = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: 'quickpass.inkSoft',
    paddingX: '10px',
    paddingTop: '6px',
  },
})

const SectionList = styled('ul', {
  base: {
    listStyle: 'none',
    padding: '0',
    margin: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const Row = styled('li', {
  base: {
    position: 'relative',
    display: 'flex',
    gap: '12px',
    paddingX: '14px',
    paddingY: '12px',
    borderRadius: 'md',
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#F0EDFA',
    cursor: 'pointer',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, transform {durations.fast} {easings.appleStandard}',
    _hover: {
      borderColor: 'quickpass.primarySoft',
      transform: 'translateY(-1px)',
    },
  },
  variants: {
    isUnread: {
      true: {
        backgroundColor: 'quickpass.surfaceCool',
        borderColor: 'quickpass.primarySoft',
      },
    },
  },
})

const RowAccent = styled('span', {
  base: {
    position: 'absolute',
    left: '0',
    top: '12px',
    bottom: '12px',
    width: '3px',
    borderRadius: 'pill',
    backgroundColor: 'quickpass.primary',
  },
  variants: {
    tone: {
      primary: { backgroundColor: 'quickpass.primary' },
      success: { backgroundColor: 'quickpass.success' },
      warmth: { backgroundColor: 'quickpass.warmth' },
      celebration: { backgroundColor: 'quickpass.celebration' },
    },
  },
  defaultVariants: {
    tone: 'primary',
  },
})

const RowGlyph = styled('span', {
  base: {
    width: '36px',
    height: '36px',
    borderRadius: 'pill',
    backgroundColor: 'quickpass.surfaceCool',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: '0',
  },
})

const RowBody = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: '1',
    minWidth: '0',
  },
})

const RowHeader = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
})

const RowTitle = styled('p', {
  base: {
    fontFamily: 'display',
    fontSize: '14px',
    fontWeight: '600',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.1px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  variants: {
    isUnread: {
      true: {
        fontWeight: '700',
      },
    },
  },
})

const RowTime = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    flexShrink: '0',
  },
})

const RowText = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.45',
    lineClamp: '3',
  },
})

const EmptyBlock = styled('div', {
  base: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    paddingX: '24px',
    gap: '10px',
  },
})

const EmptyEmoji = styled('span', {
  base: {
    fontSize: '40px',
    lineHeight: '1',
  },
})

const EmptyTitle = styled('p', {
  base: {
    fontFamily: 'display',
    fontSize: '17px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
  },
})

const EmptyBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    margin: '0',
    maxWidth: '280px',
    lineHeight: '1.5',
  },
})

export {
  Backdrop,
  Panel,
  Header,
  HeaderText,
  Title,
  Subtitle,
  HeaderActions,
  TextButton,
  CloseButton,
  ScrollArea,
  SectionLabel,
  SectionList,
  Row,
  RowAccent,
  RowGlyph,
  RowBody,
  RowHeader,
  RowTitle,
  RowTime,
  RowText,
  EmptyBlock,
  EmptyEmoji,
  EmptyTitle,
  EmptyBody,
}
